"use client";

import * as React from "react";

import { useIsTestEnvironment } from "@/hooks/use-is-test-environment";
import { useI18n } from "@/i18n/use-i18n";
import { cn } from "@/lib/utils";

type TestEnvironmentOnlyProps = {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
  label?: string;
  description?: string;
  hideWhenDisabled?: boolean;
  showBadge?: boolean;
  enabled?: boolean;
  variant?: "panel" | "inline" | "button" | "nav" | "field";
  effect?: "glow" | "stripe" | "invert";
};

const variantClassMap: Record<NonNullable<TestEnvironmentOnlyProps["variant"]>, string> = {
  panel: "rounded-2xl p-4 sm:p-5",
  inline: "inline-flex max-w-full rounded-xl px-3 py-2",
  button: "inline-flex max-w-full rounded-xl",
  nav: "block w-full rounded-lg",
  field: "rounded-2xl p-3",
};

export function TestEnvironmentOnly({
  children,
  className,
  label,
  description,
  hideWhenDisabled = true,
  showBadge = true,
  enabled,
  variant = "panel",
  effect = "glow",
}: TestEnvironmentOnlyProps) {
  const { t } = useI18n();
  const isTestEnvironment = useIsTestEnvironment();

  if (!isTestEnvironment) {
    return hideWhenDisabled ? null : <>{children}</>;
  }

  if (enabled === false) {
    return hideWhenDisabled ? null : <>{children}</>;
  }

  const resolvedLabel = (label ?? t("common.testEnvironmentBadge")).toUpperCase();
  const resolvedDescription = description ?? t("common.testEnvironmentDescription");
  const Wrapper = variant === "inline" || variant === "button" ? "span" : "div";
  const isCompact = variant === "button" || variant === "inline" || variant === "nav";

  return (
    <Wrapper
      className={cn(
        "relative",
        !isCompact && "block",
        variantClassMap[variant],
        className
      )}
      data-test-environment-only="true"
      data-test-variant={variant}
      data-test-effect={effect}
      title={resolvedDescription}
    >
      {showBadge ? (
        <span
          className={cn(
            "pointer-events-none absolute z-20 rounded-md bg-[#DC2626] px-1.5 py-0.5 text-[10px] font-black uppercase leading-none tracking-[0.08em] text-white shadow-sm",
            isCompact ? "right-1 top-1" : "right-2 top-2"
          )}
        >
          {resolvedLabel}
        </span>
      ) : null}

      <div className={cn("relative z-10", !isCompact && showBadge && "pt-5")}>
        {children}
      </div>
    </Wrapper>
  );
}
